import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import ReviewsBar from "../components/ReviewsBar";
import "../styles/parkingVsTaxi.css";

const OFFICIAL_URL = "https://www.manchesterairport.co.uk/getting-to-and-from/pick-up-and-drop-off/";
const PAYMENT_URL = "https://pay.manchesterairport.co.uk/latepaysearch/vrnsearch";
const PICKUP_PAYMENT_URL = "https://pay.manchesterairport.co.uk/latepaysearch/vrnsearch";
const JETPARKS_URL = "https://www.manchesterairport.co.uk/parking/jetparks/";
const SHORT_STAY_URL = "https://www.manchesterairport.co.uk/parking/short-stay/";
const TURN_UP_URL = "https://www.manchesterairport.co.uk/parking/turn-up-and-park/";
const PREBOOK_URL = "https://booking.parking.manchesterairport.co.uk/";

function money(value) {
  return value == null ? "Check official price" : value === 0 ? "Free" : `£${value.toFixed(2)}`;
}

export default function ManchesterDropoffCalculator() {
  const [journeyType, setJourneyType] = useState("dropoff");
  const [terminal, setTerminal] = useState("T2");
  const [minutes, setMinutes] = useState(5);
  const [blueBadge, setBlueBadge] = useState(false);

  const options = useMemo(() => {
    const stay = Number(minutes) || 5;
    if (journeyType === "pickup") {
      const pickupPostcode = terminal === "T2" ? "M90 5PR" : "M90 1QX";
      return [
        {
          key: "pickup",
          name: `${terminal} Express Pick Up`,
          cost: stay <= 15 ? 6.6 : null,
          detail: `Use postcode ${pickupPostcode}. Maximum stay is 15 minutes; pay online by midnight the following day.`,
          recommended: stay <= 15,
        },
        {
          key: "turn-up",
          name: "Turn Up and Park",
          cost: null,
          detail: terminal === "T2" ? "Use the first or second floor when you need longer than 15 minutes." : "Use official parking when you need longer than the 15-minute Express Pick Up limit.",
          recommended: stay > 15,
        },
        {
          key: "prebook",
          name: "Pre-book parking",
          cost: null,
          detail: "Plan ahead and check an exact parking price for your arrival time.",
          recommended: false,
        },
      ];
    }
    const forecourtCost = blueBadge ? 0 : stay <= 5 ? 5.5 : stay <= 10 ? 6.5 : stay <= 30 ? 25 : null;
    return [
      {
        key: "forecourt",
        name: `${terminal} departures drop-off`,
        cost: forecourtCost,
        detail: blueBadge
          ? "Valid Blue Badge drop-off exemption selected. The travelling passenger must show the badge to the traffic marshals when entering the forecourt."
          : stay > 30
          ? "The express drop-off zone is not suitable beyond 30 minutes. Use an official car park."
          : `Closest option to Departures. Use postcode ${terminal === "T2" ? "M90 5PH" : "M90 1NN"} and pay online by midnight the following day.`,
        recommended: blueBadge || stay <= 10,
      },
      {
        key: "free",
        name: "Free drop-off area",
        cost: 0,
        detail: "JetParks 1, M90 5AZ. The free 24/7 shuttle takes around 6 minutes and operates one way from the car park to the terminal.",
        recommended: !blueBadge && stay > 10,
      },
      {
        key: "short",
        name: "Short Stay parking",
        cost: null,
        detail: "Use when you need more time or want to accompany the passenger into the terminal.",
        recommended: false,
      },
    ];
  }, [blueBadge, journeyType, minutes, terminal]);

  return (
    <div className="train-page">
      <Helmet>
        <title>Manchester Airport Drop-off &amp; Pick-up Calculator | Compare Charges</title>
        <meta name="description" content="Compare Manchester Airport terminal drop-off and pick-up charges, the free shuttle drop-off area and parking alternatives." />
        <link rel="canonical" href="https://www.airportjourneyplanner.com/manchester-airport/drop-off-calculator" />
      </Helmet>

      <div className="train-container" style={{ maxWidth: 1040 }}>
        <header style={{ marginBottom: 22 }}>
          <div style={{ color: "#16756b", fontWeight: 900, textTransform: "uppercase", fontSize: 13 }}>Manchester Airport drop-off</div>
          <h1 style={{ marginBottom: 8 }}>Manchester Airport drop-off and pick-up calculator</h1>
          <p style={{ color: "#475569", maxWidth: 760 }}>
            Compare the terminal forecourt, free shuttle drop-off and Short Stay option before setting off.
          </p>
        </header>

        <section style={{ padding: 18, border: "1px solid #dbe5f1", borderRadius: 16, background: "#f8fbff", marginBottom: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 14 }}>
            <label style={{ fontWeight: 800 }}>Journey type
              <select value={journeyType} onChange={(event) => { setJourneyType(event.target.value); setMinutes(event.target.value === "pickup" ? 15 : 5); }} style={{ display: "block", width: "100%", marginTop: 7, padding: 11, borderRadius: 9, border: "1px solid #cbd5e1" }}>
                <option value="dropoff">Drop off</option>
                <option value="pickup">Pick up</option>
              </select>
            </label>
            <label style={{ fontWeight: 800 }}>Terminal
              <select value={terminal} onChange={(event) => setTerminal(event.target.value)} style={{ display: "block", width: "100%", marginTop: 7, padding: 11, borderRadius: 9, border: "1px solid #cbd5e1" }}>
                <option value="T2">Terminal 2</option>
                <option value="T3">Terminal 3</option>
              </select>
            </label>
            <label style={{ fontWeight: 800 }}>Expected stop
              <select value={minutes} onChange={(event) => setMinutes(Number(event.target.value))} style={{ display: "block", width: "100%", marginTop: 7, padding: 11, borderRadius: 9, border: "1px solid #cbd5e1" }}>
                {journeyType === "dropoff" ? <><option value={5}>Up to 5 minutes</option><option value={10}>Up to 10 minutes</option><option value={30}>Up to 30 minutes</option><option value={45}>More than 30 minutes</option></> : <><option value={15}>Up to 15 minutes</option><option value={30}>More than 15 minutes</option></>}
              </select>
            </label>
            {journeyType === "dropoff" ? <label style={{ display: "flex", alignItems: "center", gap: 9, fontWeight: 800, paddingTop: 25 }}>
              <input type="checkbox" checked={blueBadge} onChange={(event) => setBlueBadge(event.target.checked)} /> Travelling passenger has a valid Blue Badge
            </label> : null}
          </div>
        </section>

        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 14 }}>
          {options.map((option) => (
            <article key={option.key} style={{ border: option.recommended ? "2px solid #16a34a" : "1px solid #dbe5f1", borderRadius: 16, padding: 18, background: option.recommended ? "#f0fdf4" : "#fff" }}>
              {option.recommended ? <div style={{ color: "#15803d", fontWeight: 900, fontSize: 12, textTransform: "uppercase" }}>Recommended for this stay</div> : null}
              <h2 style={{ fontSize: 20, margin: "8px 0" }}>{option.name}</h2>
              <strong style={{ display: "block", fontSize: 26, color: "#0b2b64", marginBottom: 9 }}>{money(option.cost)}</strong>
              <p style={{ color: "#475569", lineHeight: 1.5 }}>{option.detail}</p>
            </article>
          ))}
        </section>

        <div style={{ marginTop: 18, padding: 15, borderRadius: 12, background: "#fff7ed", border: "1px solid #fed7aa", color: "#7c2d12" }}>
          <strong>Important:</strong> Charges and access rules can change. Picking passengers up in a drop-off zone can result in a £100 charge. Express Pick Up has a 15-minute maximum and a £60 overstay charge. Confirm signage and current prices on the official airport page.
        </div>

        <section style={{ marginTop: 18, padding: 18, border: "1px solid #dbe5f1", borderRadius: 16, background: "#fff" }}>
          <h2 style={{ marginTop: 0 }}>Payment, Blue Badge and PCN information</h2>
          <ul style={{ margin: 0, paddingLeft: 20, color: "#475569", lineHeight: 1.65 }}>
            <li>Pay online or by phone on <a href="tel:03459013318">03459 013318</a>.</li>
            <li>Payment is due by midnight the day after the visit. Cash, cheque and payment at an exit barrier are not available.</li>
            <li>Non-payment can result in a £100 Parking Charge Notice, reduced to £60 when paid within 14 days.</li>
            <li>Blue Badge exemption applies to drop-off only. The travelling passenger must produce the valid badge to the traffic marshals when entering the forecourt.</li>
            <li>Appeals are handled through <a href="https://payments.apcoa.co.uk/" target="_blank" rel="noreferrer">APCOA ↗</a>.</li>
            <li>Read the airport’s <a href="https://www.manchesterairport.co.uk/parking/pick-up-and-drop-off-info/blue-badge-holders/" target="_blank" rel="noreferrer">Blue Badge guidance ↗</a>.</li>
          </ul>
        </section>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 18 }}>
          <a href={OFFICIAL_URL} target="_blank" rel="noreferrer" className="train-primary-cta">Check official Manchester Airport rules ↗</a>
          <a href={PAYMENT_URL} target="_blank" rel="noreferrer" className="train-secondary-cta">Pay a drop-off charge ↗</a>
          <a href={PICKUP_PAYMENT_URL} target="_blank" rel="noreferrer" className="train-secondary-cta">Pay a pick-up charge ↗</a>
          <a href={JETPARKS_URL} target="_blank" rel="noreferrer" className="train-secondary-cta">JetParks free drop-off ↗</a>
          <a href={SHORT_STAY_URL} target="_blank" rel="noreferrer" className="train-secondary-cta">Short Stay ↗</a>
          <a href={TURN_UP_URL} target="_blank" rel="noreferrer" className="train-secondary-cta">Turn Up and Park ↗</a>
          <a href={PREBOOK_URL} target="_blank" rel="noreferrer" className="train-secondary-cta">Pre-book parking ↗</a>
        </div>

        <p style={{ marginTop: 15, color: "#64748b", fontSize: 12 }}>
          Prices checked against Manchester Airport’s published Pick Up &amp; Drop Off page on 10 September 2026.
        </p>
        <div style={{ marginTop: 36 }}><ReviewsBar /></div>
      </div>
    </div>
  );
}
